import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { CalendarClock, X, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Floating action button that lets Atendimento agendar (ou reagendar) a
 * Reunião Técnica de qualquer scroll dentro da página de um lead — sem ter
 * de subir/descer até à secção "Agendar Reunião Técnica".
 *
 * Só renderiza quando estamos dentro de uma rota /leads/:id/*. Reutiliza
 * os endpoints existentes:
 *   • GET  /api/users?role=consultor  → lista de Consultores de Cuidados
 *   • GET  /api/graph/calendar/available-slots?date=&manager_id=  → slots livres
 *   • POST /api/leads/{id}/schedule  → cria/actualiza a marcação (+ Teams event)
 *
 * 04/08/2026 — Post-restructure, RT scheduling picks a Consultor de Cuidados
 * (evaluates needs + presents proposal), not a Gestor. The `manager_id`
 * param on the API is kept for backward compat but the server stores it
 * in the ``assigned_consultor_id`` slot. Passing ``consultor_id`` here
 * is preferred and explicit.
 *
 * Depois de agendar com sucesso, dispara ``window.dispatchEvent("lead-updated")``
 * para que o LeadDetail recarregue o percurso/timeline sem full reload.
 */
export default function RTScheduleFab() {
  const { id } = useParams();
  const location = useLocation();
  const leadId = id || (location.pathname.match(/\/leads\/([\w-]+)/) || [])[1];

  const [open, setOpen] = useState(false);
  const [managers, setManagers] = useState([]);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [managerId, setManagerId] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null); // { scheduled_qt_at, teams_event_id, teams_join_url }

  // Load Consultores de Cuidados when drawer opens (or when leadId changes).
  useEffect(() => {
    if (!open || !leadId) return;
    let mounted = true;
    api.get("/users")
      .then(r => {
        if (!mounted) return;
        const list = Array.isArray(r.data) ? r.data : [];
        // 26/02/2026 — Só consultores efectivos aparecem no dropdown.
        // `is_consultor_effective` (calculado no backend) captura tanto
        // `role="consultor"` como o Consultor Temporário (validade
        // definida em `temporary_consultor_until`). Utilizadores
        // inactivos/soft-deleted são excluídos.
        setManagers(list.filter(u =>
          u.is_consultor_effective === true && u.active !== false
        ));
      })
      .catch(() => { if (mounted) setManagers([]); });
    // Prefetch current lead state so we can pre-fill date/time/consultor.
    api.get(`/leads/${leadId}`)
      .then(r => {
        if (!mounted) return;
        const lead = r.data || {};
        setExisting({
          scheduled_qt_at: lead.scheduled_qt_at || null,
          teams_event_id: lead.teams_event_id || null,
          teams_join_url: lead.teams_join_url || null,
        });
        if (lead.scheduled_qt_at) {
          setDate((lead.scheduled_qt_at || "").slice(0, 10));
          setTime((lead.scheduled_qt_at || "").slice(11, 16));
        }
        // Prefer the new consultor slot; fall back to the legacy manager
        // field for leads created before the restructure.
        const preId = lead.assigned_consultor_id || lead.assigned_manager_id;
        if (preId) setManagerId(preId);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [open, leadId]);

  // Availability lookup — mirrors LeadDetail's useEffect.
  useEffect(() => {
    if (!open) return;
    if (!date || !managerId) { setSlots([]); setSlotsError(""); return; }
    let mounted = true;
    setSlotsLoading(true); setSlotsError("");
    api.get("/graph/calendar/available-slots", { params: { date, manager_id: managerId } })
      .then(r => { if (mounted) setSlots(r.data?.slots || []); })
      .catch(e => {
        if (mounted) {
          setSlots([]);
          setSlotsError(e.response?.data?.detail || "Não foi possível verificar disponibilidade");
        }
      })
      .finally(() => { if (mounted) setSlotsLoading(false); });
    return () => { mounted = false; };
  }, [open, date, managerId]);

  if (!leadId) return null;

  const submit = async () => {
    if (!date || !time) { toast.error("Indique data e hora"); return; }
    if (!managerId) { toast.error("Escolha o Consultor de Cuidados"); return; }
    const mgr = managers.find(m => m.id === managerId);
    setSaving(true);
    try {
      await api.post(`/leads/${leadId}/schedule`, {
        scheduled_at: `${date}T${time}:00`,
        // Send the explicit consultor slot; the server will fall back
        // to manager_id/name for old clients, but new writes must land
        // in ``assigned_consultor_*`` directly.
        consultor_id: mgr?.id,
        consultor_name: mgr?.name,
      });
      toast.success("Reunião Técnica agendada");
      // Let any open LeadDetail refresh its own state without a full reload.
      window.dispatchEvent(new CustomEvent("lead-updated", { detail: { id: leadId } }));
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao agendar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="rt-fab"
          title="Agendar Reunião Técnica"
          aria-label="Agendar Reunião Técnica"
          className="fixed top-[calc(50%-235px)] right-0 z-40 flex flex-col items-center gap-2 py-4 px-2 rounded-l-2xl bg-card border border-r-0 border-border shadow-[0_10px_40px_-10px_rgba(15,27,44,0.14)] hover:bg-muted transition-colors group"
        >
          <CalendarClock className="w-4 h-4 text-primary" />
          {existing?.scheduled_qt_at && (
            <span className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-[hsl(var(--success))] text-primary-foreground" title="Já marcada">
              <CheckCircle2 className="w-3 h-3" />
            </span>
          )}
          <span className="[writing-mode:vertical-rl] rotate-180 text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground group-hover:text-foreground">
            Agendar RT
          </span>
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 flex justify-end"
          data-testid="rt-fab-drawer"
        >
          {/* backdrop */}
          <button
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="flex-1 bg-foreground/30 backdrop-blur-sm"
            data-testid="rt-fab-backdrop"
          />
          {/* panel */}
          <div className="w-full sm:w-[440px] bg-card h-full shadow-2xl border-l border-border flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/40">
              <div className="flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-primary" />
                <div>
                  <div className="font-heading text-sm text-foreground">Agendar Reunião Técnica</div>
                  <div className="text-[10px] text-muted-foreground">Slots reais do Outlook do Consultor de Cuidados</div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="rt-fab-close"
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {existing?.scheduled_qt_at && (
                <div className="bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 rounded-lg px-3 py-2 text-[12px] text-[hsl(var(--success))]" data-testid="rt-fab-existing">
                  Já existe reunião marcada em <b>{existing.scheduled_qt_at.slice(0, 10)} · {existing.scheduled_qt_at.slice(11, 16)}</b>.
                  Ao guardar novos valores, o evento Outlook é <b>reagendado</b>.
                </div>
              )}

              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading">Data</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  data-testid="rt-fab-date"
                  className="w-full mt-1 bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading">Consultor de Cuidados</label>
                <select
                  value={managerId}
                  onChange={(e) => setManagerId(e.target.value)}
                  data-testid="rt-fab-manager"
                  className="w-full mt-1 bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary"
                >
                  <option value="">Escolha um consultor…</option>
                  {managers.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>

              {/* Slots panel */}
              {date && managerId && (
                <div className="rounded-lg border border-border bg-muted/40 p-3" data-testid="rt-fab-slots">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold font-heading">
                      Slots livres · {new Date(date).toLocaleDateString("pt-PT")}
                    </div>
                    {slotsLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-foreground" />}
                  </div>
                  {slotsError && (
                    <div className="text-[11px] text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/25 rounded-md px-2.5 py-1.5 inline-flex items-center gap-1.5" data-testid="rt-fab-slots-error">
                      <AlertTriangle className="w-3 h-3" /> {slotsError}
                    </div>
                  )}
                  {!slotsError && !slotsLoading && slots.length === 0 && (
                    <div className="text-[11px] text-muted-foreground">Sem slots livres neste dia. Escolha outra data ou defina manualmente a hora abaixo.</div>
                  )}
                  {!slotsError && slots.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {slots.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setTime(s)}
                          data-testid={`rt-fab-slot-${s}`}
                          className={`text-xs rounded-full px-3 py-1.5 border transition ${
                            time === s
                              ? "bg-foreground text-background border-[#0F172A]"
                              : "bg-card text-foreground border-border hover:bg-muted/40"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading">Hora</label>
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  data-testid="rt-fab-time"
                  className="w-full mt-1 bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary"
                />
                <div className="text-[10px] text-muted-foreground mt-1">
                  Escolha um slot livre acima ou defina manualmente aqui.
                </div>
              </div>
            </div>

            <div className="border-t border-border bg-card p-4 flex items-center justify-end gap-2">
              <button
                onClick={() => setOpen(false)}
                data-testid="rt-fab-cancel"
                className="text-[12px] text-muted-foreground hover:text-foreground px-3 py-2"
              >
                Cancelar
              </button>
              <button
                onClick={submit}
                disabled={saving}
                data-testid="rt-fab-submit"
                className="inline-flex items-center gap-2 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground px-4 py-2 text-sm font-heading font-semibold tracking-tight"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
                {existing?.scheduled_qt_at ? "Reagendar" : "Agendar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
